import React, { useState, useRef, useEffect } from 'react';
import { UserButton, useClerk, useUser } from '@clerk/clerk-react';
import { Bell, Users, User, RefreshCw, MessageSquare, Settings2 } from 'lucide-react';
import './Dashboard.css';
import ProfileTab from './DashboardTabs/ProfileTab';
import SyncTab from './DashboardTabs/SyncTab';
import CoachTab from './DashboardTabs/CoachTab';
import SettingsTab from './DashboardTabs/SettingsTab';
import { YDKYLogo } from './Navbar';
import { getPersonalityTheme } from '../CardFiles/theme-config';

export const Dashboard = ({ user: propsUser, analysisResult, hasCompletedCard, onViewCards, onResumeTest, onRestart, onNavigateToCompare }) => {
    const { signOut } = useClerk();
    const { user: clerkUser } = useUser();

    // Always prefer fresh clerkUser if available
    const activeUser = clerkUser || propsUser;

    // Read from sessionStorage or fallback to 'profile'
    const [activeTab, setActiveTabState] = useState(() => {
        return sessionStorage.getItem('dashboard_active_tab') || 'profile';
    });

    const setActiveTab = (tab) => {
        setActiveTabState(tab);
        sessionStorage.setItem('dashboard_active_tab', tab);
    };

    const tabContentRef = useRef(null);

    // Scroll tab content to top on every tab switch
    useEffect(() => {
        if (tabContentRef.current) {
            tabContentRef.current.scrollTop = 0;
        }
    }, [activeTab]);

    return (
        <div className="dashboard-container">

            {/* Header */}
            <header className="dashboard-header" style={{ justifyContent: 'space-between' }}>
                <div className="navbar-brand" style={{ cursor: 'pointer', paddingLeft: '1rem', paddingTop: '0.5rem' }} onClick={() => window.location.href='/'}>
                    <YDKYLogo />
                </div>

                <div className="header-actions">
                    <div className="clerk-user-button-wrapper" style={{ marginLeft: '12px' }}>
                        <UserButton
                            afterSignOutUrl="/"
                            appearance={{
                                elements: {
                                    avatarBox: { width: 36, height: 36 },
                                    userButtonPopoverCard: {
                                        background: 'rgba(30, 30, 50, 0.95)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                    },
                                    userButtonPopoverActionButton: {
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
                                    },
                                    userButtonPopoverActionButtonText: {
                                        color: 'rgba(255, 255, 255, 0.9)',
                                    },
                                    userButtonPopoverFooter: { display: 'none' },
                                }
                            }}
                        />
                    </div>
                </div>
            </header>

            {/* Main content area - Individual tabs handle their own scrolling */}
            <main className="dashboard-main-content">
                <div className="tab-container" style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
                    <ProfileTab user={activeUser} analysisResult={analysisResult} hasCompletedCard={hasCompletedCard} onViewCards={onViewCards} onResumeTest={onResumeTest} onRestart={onRestart} />
                </div>
                
                <div className="tab-container" style={{ display: activeTab === 'sync' ? 'block' : 'none' }}>
                    <SyncTab isActive={activeTab === 'sync'} user={activeUser} analysisResult={analysisResult} onRestart={onRestart} onCompareRequested={onNavigateToCompare} />
                </div>
                
                <div className="tab-container coach-tab-wrapper" style={{ display: activeTab === 'coach' ? 'flex' : 'none' }}>
                    <CoachTab user={activeUser} analysisResult={analysisResult} />
                </div>
                
                <div className="tab-container" style={{ display: activeTab === 'account' ? 'block' : 'none' }}>
                    <SettingsTab 
                        user={activeUser} 
                        analysisResult={analysisResult} 
                        onRestart={onRestart} 
                        setActiveTab={setActiveTab}
                    />
                </div>
            </main>

            {/* Bottom Navigation */}
            <nav className="bottom-nav">
                <button
                    className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    <span className="nav-icon"><User size={20} /></span>
                    <span className="nav-label">You</span>
                </button>
                <button
                    className={`nav-item ${activeTab === 'sync' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sync')}
                >
                    <span className="nav-icon"><RefreshCw size={20} /></span>
                    <span className="nav-label">Connections</span>
                </button>
                <button
                    className={`nav-item ${activeTab === 'coach' ? 'active' : ''}`}
                    onClick={() => setActiveTab('coach')}
                >
                    <span className="nav-icon"><MessageSquare size={20} /></span>
                    <span className="nav-label">Coach</span>
                </button>
                <button
                    className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
                    onClick={() => setActiveTab('account')}
                >
                    <span className="nav-icon"><Settings2 size={20} /></span>
                    <span className="nav-label">Settings</span>
                </button>
            </nav>
        </div>
    );
};

export default Dashboard;
